import type { ShakaPlayer } from './types';

import { cacheResponse, getCachedResponse } from './videoSegmentCache';

interface ShakaNetworkResponse {
	uri: string;
	originalUri: string;
	data: ArrayBuffer;
	status: number;
	headers: Record<string, string>;
	fromCache?: boolean;
}

interface ShakaNetworkRequest {
	method?: string;
	headers?: HeadersInit;
	body?: BodyInit | null;
}

interface ShakaModule {
	polyfill: { installAll(): void };
	Player: {
		isBrowserSupported(): boolean;
		new (el?: HTMLMediaElement): ShakaPlayer;
	};
	net: {
		NetworkingEngine: {
			registerScheme(
				scheme: string,
				handler: (uri: string, request: ShakaNetworkRequest) => unknown,
				priority: number
			): void;
			PluginPriority: { APPLICATION: number };
		};
	};
	util: {
		Error: {
			new (
				severity: number,
				category: number,
				code: number,
				...args: unknown[]
			): Error;
			Severity: { RECOVERABLE: number };
			Category: { NETWORK: number };
			Code: { BAD_HTTP_STATUS: number; TIMEOUT: number };
		};
		AbortableOperation: {
			new (
				promise: Promise<ShakaNetworkResponse>,
				abort: () => Promise<void>
			): unknown;
		};
	};
}

let shakaInstance: ShakaModule | null = null;
let cacheInitialized = false;

// ---------------------------------------------------------------------------
// Active player registry — used to prioritise network requests.
//
// When a player is actively playing (user hover / visible), it registers its
// URL directory prefix here. The scheme handler gives those requests
// priority: 'high' and all other HLS requests priority: 'low', so background
// preload segments don't compete with the video the user is watching.
// ---------------------------------------------------------------------------
const activePlayerPrefixes = new Set<string>();

function urlDirPrefix(url: string): string {
	const clean = url.split('?')[0] ?? url;
	return clean.substring(0, clean.lastIndexOf('/') + 1);
}

export function registerActivePlayer(videoUrl: string): void {
	activePlayerPrefixes.add(urlDirPrefix(videoUrl));
}

export function unregisterActivePlayer(videoUrl: string): void {
	activePlayerPrefixes.delete(urlDirPrefix(videoUrl));
}

function isActiveRequest(uri: string): boolean {
	const prefix = urlDirPrefix(uri);
	for (const active of activePlayerPrefixes) {
		if (prefix.startsWith(active) || active.startsWith(prefix)) return true;
	}
	return false;
}

// ---------------------------------------------------------------------------
// Bandwidth persistence — survives page reloads so Shaka starts at the right
// quality on the first segment instead of guessing. Follows YouTube's approach:
// even on slow networks, use the real historical estimate rather than an
// optimistic default that causes immediate downgrade.
// ---------------------------------------------------------------------------
const BANDWIDTH_STORAGE_KEY = 'bifrost_abr_bps';
const BANDWIDTH_TTL_MS = 60 * 60 * 1000; // 1 hour

// Set to true once the cold-start probe has run in this page session.
// The probe always runs once per page load regardless of saved history,
// ensuring the initial quality choice reflects the CURRENT network,
// not a fast session from hours ago.
let sessionProbeCompleted = false;

export function isSessionProbeCompleted(): boolean {
	if (!import.meta.client) return true; // skip probe on SSR
	return sessionProbeCompleted;
}

export function markSessionProbeCompleted(): void {
	if (!import.meta.client) return;
	sessionProbeCompleted = true;
}
const BANDWIDTH_MIN_SEGMENT_BYTES = 10_000; // ignore tiny manifests, only measure real segments
let lastBandwidthSaveAt = 0;

export function getSavedBandwidthEstimate(): number | null {
	if (!import.meta.client) return null;
	try {
		const raw = localStorage.getItem(BANDWIDTH_STORAGE_KEY);
		if (!raw) return null;
		const { bps, ts } = JSON.parse(raw) as { bps: number; ts: number };
		if (Date.now() - ts > BANDWIDTH_TTL_MS) return null;
		return bps;
	} catch {
		return null;
	}
}

function saveBandwidthEstimate(bytes: number, durationMs: number): void {
	if (!import.meta.client) return;
	if (bytes < BANDWIDTH_MIN_SEGMENT_BYTES || durationMs <= 0) return;

	const now = Date.now();
	if (now - lastBandwidthSaveAt < 10_000) return;
	lastBandwidthSaveAt = now;

	const bps = Math.round((bytes * 8 * 1000) / durationMs);
	try {
		localStorage.setItem(
			BANDWIDTH_STORAGE_KEY,
			JSON.stringify({ bps, ts: now })
		);
	} catch {
		// Ignore storage errors (private mode, quota)
	}
}

// A resource-timing entry only substitutes for the probe when it moved enough
// real bytes over the network to be a meaningful sample (same floor as
// BANDWIDTH_MIN_SEGMENT_BYTES elsewhere, but for reused transfers).
const MIN_REUSABLE_TRANSFER_BYTES = 100_000;

/**
 * Derives a bandwidth sample from an already-completed transfer of this
 * segment (the SSR `<link rel="preload">`), instead of downloading another
 * 50 KB of the same file. The preload URL carries no `_cb`, the probe URL
 * does — strip it before matching. Returns null when no reusable transfer
 * exists (cache hits have transferSize 0 and are skipped: they measure disk,
 * not network).
 *
 * The preload downloads with fetchpriority=low, so contention can bias this
 * sample LOW — acceptable: a conservative estimate picks a safer quality.
 */
function estimateFromExistingTransfer(segmentUrl: string): number | null {
	try {
		const canonical = new URL(segmentUrl);
		canonical.searchParams.delete('_cb');
		const entries = performance.getEntriesByName(
			canonical.toString()
		) as PerformanceResourceTiming[];
		const entry = entries.find(
			e => e.transferSize >= MIN_REUSABLE_TRANSFER_BYTES && e.responseEnd > 0
		);
		if (!entry) return null;
		const durationMs = entry.responseEnd - entry.startTime;
		if (durationMs <= 0) return null;
		const bps = Math.round((entry.transferSize * 8 * 1000) / durationMs);
		lastBandwidthSaveAt = 0; // allow immediate save regardless of 10s throttle
		saveBandwidthEstimate(entry.transferSize, durationMs);
		return bps;
	} catch {
		return null;
	}
}

const MAX_PLAUSIBLE_NETWORK_BPS = 50_000_000;
function isRealNetworkSample(
	uri: string,
	bytes: number,
	durationMs: number
): boolean {
	if (durationMs <= 0) return false;
	try {
		const entries = performance.getEntriesByName(
			uri
		) as PerformanceResourceTiming[];
		const last = entries[entries.length - 1];
		if (last) return last.transferSize >= MIN_REUSABLE_TRANSFER_BYTES;
	} catch {
		// noop
	}
	return (bytes * 8 * 1000) / durationMs <= MAX_PLAUSIBLE_NETWORK_BPS;
}

export async function saveBandwidthEstimateFromProbe(
	segmentUrl: string,
	signal: AbortSignal
): Promise<number | null> {
	// The SSR preload may have just transferred this exact segment — measuring
	// that transfer is free, while probing re-downloads 50 KB of it (the probe
	// URL's `_cb` + Range header mean the two can never share a cache entry).
	const reused = estimateFromExistingTransfer(segmentUrl);
	if (reused !== null) return reused;

	try {
		const start = performance.now();
		const res = await fetch(segmentUrl, {
			headers: { Range: `bytes=0-${PROBE_BYTES - 1}` },
			cache: 'no-store', // must measure real network, not HTTP cache
			priority: 'high',
			signal
		} as RequestInit);
		if (!res.ok) return null;
		const data = await res.arrayBuffer();
		const durationMs = performance.now() - start;
		if (durationMs <= 0 || data.byteLength < 1000) return null;
		const bps = Math.round((data.byteLength * 8 * 1000) / durationMs);
		lastBandwidthSaveAt = 0; // allow immediate save regardless of 10s throttle
		saveBandwidthEstimate(data.byteLength, durationMs);
		return bps;
	} catch (err) {
		if ((err as Error)?.name !== 'AbortError') {
			console.error('[shakaLoader] saveBandwidthEstimateFromProbe failed', {
				message: (err as Error)?.message,
				stack: (err as Error)?.stack
			});
		}
		return null;
	}
}

// ---------------------------------------------------------------------------
// Periodic bandwidth probe — detects network improvement on slow connections.
//
// Runs every 30s when saved bandwidth is below 1080p threshold (~4 Mbps).
// Downloads 50KB of a real CDN segment via Range request to measure speed.
// Works for both Shaka and iOS native HLS: Shaka players get more aggressive
// ABR targets; iOS players re-check quality upgrade eligibility.
//
// Registered callbacks receive the new bps estimate and decide what to do.
// ---------------------------------------------------------------------------
const PROBE_THRESHOLD_BPS = 4_000_000; // only probe when below 1080p threshold
const PROBE_INTERVAL_MS = 30_000;
const PROBE_BYTES = 51_200; // 50 KB
const PROBE_IMPROVEMENT_FACTOR = 1.5; // notify only if 50% better than saved

type BandwidthCallback = (bps: number) => void;
const bandwidthCallbacks = new Set<BandwidthCallback>();
let probeTimer: ReturnType<typeof setTimeout> | null = null;
let lastProbeSegmentUrl: string | null = null; // last real .ts URL seen — reused for probe

export function onBandwidthImprovement(cb: BandwidthCallback): () => void {
	bandwidthCallbacks.add(cb);
	return () => bandwidthCallbacks.delete(cb);
}

async function runBandwidthProbe(): Promise<void> {
	if (!import.meta.client || !lastProbeSegmentUrl) return;

	const saved = getSavedBandwidthEstimate();
	if (saved !== null && saved >= PROBE_THRESHOLD_BPS) return; // already fast enough

	try {
		const start = performance.now();
		const res = await fetch(lastProbeSegmentUrl, {
			headers: { Range: `bytes=0-${PROBE_BYTES - 1}` },
			cache: 'no-store', // bypass HTTP cache — a cached response measures disk speed, not network
			priority: 'low'
		} as RequestInit);
		if (!res.ok) return;

		const data = await res.arrayBuffer();
		const durationMs = performance.now() - start;
		if (durationMs <= 0 || data.byteLength < 1000) return;

		const measuredBps = Math.round((data.byteLength * 8 * 1000) / durationMs);
		saveBandwidthEstimate(data.byteLength, durationMs);

		if (saved === null || measuredBps > saved * PROBE_IMPROVEMENT_FACTOR) {
			for (const cb of bandwidthCallbacks) cb(measuredBps);
		}
	} catch (err) {
		if ((err as Error)?.name !== 'AbortError') {
			console.error('[shakaLoader] runBandwidthProbe failed', {
				message: (err as Error)?.message,
				stack: (err as Error)?.stack
			});
		}
	} finally {
		scheduleProbe();
	}
}

function scheduleProbe(): void {
	if (!import.meta.client) return;
	if (probeTimer) clearTimeout(probeTimer);
	probeTimer = setTimeout(() => void runBandwidthProbe(), PROBE_INTERVAL_MS);
}

export function startBandwidthProbing(): void {
	scheduleProbe();
}

function stopBandwidthProbing(): void {
	if (probeTimer) {
		clearTimeout(probeTimer);
		probeTimer = null;
	}
}

export function stopBandwidthProbingIfIdle(): void {
	if (bandwidthCallbacks.size === 0) stopBandwidthProbing();
}

/** Clears the probe segment URL — call when the segment cache is cleared so
 *  the probe doesn't attempt a range request against a no-longer-cached URL. */
export function resetProbeSegmentUrl(): void {
	lastProbeSegmentUrl = null;
}

const VERSION_MAP_MAX_SIZE = 200;
const versionHashMap = new Map<string, string>();

function versionMapSet(key: string, value: string): void {
	if (versionHashMap.size >= VERSION_MAP_MAX_SIZE) {
		const firstKey = versionHashMap.keys().next().value;
		if (firstKey) versionHashMap.delete(firstKey);
	}
	versionHashMap.set(key, value);
}

function getVersionedUrl(url: string): string {
	const urlPrefix = url.substring(0, url.lastIndexOf('/'));
	const isManifest = url.includes('.m3u8');

	if (isManifest) {
		const match = url.match(/[?&]v=([^&]+)/);
		if (match?.[1]) versionMapSet(urlPrefix, match[1]);
	}

	let versionHash = versionHashMap.get(urlPrefix);

	if (!versionHash) {
		let parentPrefix = urlPrefix;
		while (
			!versionHash &&
			parentPrefix.includes('/', parentPrefix.indexOf('//') + 2)
		) {
			parentPrefix = parentPrefix.substring(0, parentPrefix.lastIndexOf('/'));
			versionHash = versionHashMap.get(parentPrefix);
		}
		if (versionHash) versionMapSet(urlPrefix, versionHash);
	}

	if (versionHash && !url.includes('v=')) {
		const isHlsFile = url.includes('.m3u8') || url.includes('.ts');
		if (isHlsFile) {
			return `${url}${url.includes('?') ? '&' : '?'}v=${versionHash}`;
		}
	}

	return url;
}

// Callbacks fired with a live bandwidth measurement mid-download.
// Used to apply ABR restrictions before the current segment finishes.
const liveBandwidthCallbacks = new Set<(bps: number) => void>();

export function onLiveBandwidth(cb: (bps: number) => void): () => void {
	liveBandwidthCallbacks.add(cb);
	return () => liveBandwidthCallbacks.delete(cb);
}

// Sentinel thrown when we abort a segment mid-download due to slow bandwidth.
// The scheme handler catches it and re-throws as a RECOVERABLE Shaka error so
// Shaka retries — with restrictions already set — at a lower quality variant.
const SLOW_DOWNLOAD_ABORT = Symbol('slow-download-abort');

async function readBodyChunked(
	response: Response,
	signal: AbortSignal,
	isSegment = false,
	onLiveMeasure?: (bps: number) => boolean // return true to abort the download
): Promise<ArrayBuffer> {
	if (!response.body) return response.arrayBuffer();

	const reader = response.body.getReader();
	const chunks: Uint8Array[] = [];
	let totalLength = 0;
	const startTime = performance.now();
	let liveFired = false;

	try {
		while (true) {
			if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
			const { done, value } = await reader.read();
			if (done) break;
			chunks.push(value);
			totalLength += value.length;

			if (isSegment && !liveFired && totalLength >= 100_000) {
				const elapsedMs = performance.now() - startTime;
				if (elapsedMs > 500) {
					const liveBps = Math.round((totalLength * 8 * 1000) / elapsedMs);
					liveFired = true;
					const shouldAbort = onLiveMeasure?.(liveBps) ?? false;
					if (shouldAbort) {
						const err = new Error('slow-download-abort') as Error &
							Record<symbol, boolean>;
						err[SLOW_DOWNLOAD_ABORT] = true;
						throw err;
					}
				}
			}
		}
	} catch (err) {
		reader.cancel().catch(() => {});
		throw err;
	}

	const combined = new Uint8Array(totalLength);
	let offset = 0;
	for (const chunk of chunks) {
		combined.set(chunk, offset);
		offset += chunk.length;
	}
	return combined.buffer;
}

function initSegmentCache(shaka: ShakaModule): void {
	if (cacheInitialized) return;

	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

	shaka.net.NetworkingEngine.registerScheme(
		'https',
		(uri: string, request: ShakaNetworkRequest) => {
			const abortController = new AbortController();

			const promise = (async () => {
				const finalUri = getVersionedUrl(uri);

				try {
					const cached = await getCachedResponse(finalUri);
					if (cached) {
						return {
							uri: finalUri,
							originalUri: uri,
							data: await cached.arrayBuffer(),
							status: 200,
							headers: {},
							fromCache: true
						};
					}
				} catch (cacheErr) {
					// Cache lookup failed, proceed with fetch
				}

				const priority = isActiveRequest(finalUri) ? 'high' : 'low';
				const fetchStart = finalUri.includes('.ts') ? performance.now() : 0;

				const response = await fetch(finalUri, {
					method: request.method || 'GET',
					headers: request.headers,
					body: request.body,
					signal: abortController.signal,
					priority
				} as RequestInit);

				if (!response.ok) {
					throw new shaka.util.Error(
						shaka.util.Error.Severity.RECOVERABLE,
						shaka.util.Error.Category.NETWORK,
						shaka.util.Error.Code.BAD_HTTP_STATUS,
						finalUri,
						response.status
					);
				}

				const isSegmentReq = finalUri.includes('.ts');

				let data: ArrayBuffer;
				try {
					data = await readBodyChunked(
						response,
						abortController.signal,
						isSegmentReq,
						isSegmentReq
							? (liveBps: number) => {
									for (const cb of liveBandwidthCallbacks) cb(liveBps);
									// Never abort based on throughput — Shaka's ABR and stall
									// detection handle quality switching. Aborting here would
									// break playback on any connection below 1.5 Mbps (e.g. 480p
									// only needs ~800 kbps).
									return false;
								}
							: undefined
					);
				} catch (err) {
					const slowAbortKey = SLOW_DOWNLOAD_ABORT as symbol;
					if ((err as Record<symbol, unknown>)[slowAbortKey]) {
						throw new shaka.util.Error(
							shaka.util.Error.Severity.RECOVERABLE,
							shaka.util.Error.Category.NETWORK,
							shaka.util.Error.Code.TIMEOUT,
							finalUri
						);
					}
					if ((err as Error)?.name !== 'AbortError') {
						console.error('[shakaLoader] readBodyChunked failed', {
							message: (err as Error)?.message,
							uri: finalUri,
							stack: (err as Error)?.stack
						});
					}
					throw err;
				}

				if (fetchStart > 0) {
					const durationMs = performance.now() - fetchStart;
					if (isRealNetworkSample(finalUri, data.byteLength, durationMs)) {
						saveBandwidthEstimate(data.byteLength, durationMs);
					}
					lastProbeSegmentUrl = finalUri;
				}

				const isHlsResource =
					finalUri.includes('.m3u8') || finalUri.includes('.ts');
				if (isHlsResource) {
					try {
						await cacheResponse(
							finalUri,
							new Response(data, {
								status: response.status,
								headers: response.headers
							})
						);
					} catch (cacheErr) {
						// Cache store failed, but we still return the data
					}
				}

				return {
					uri: finalUri,
					originalUri: uri,
					data,
					status: response.status,
					headers: {}
				};
			})();

			return new shaka.util.AbortableOperation(promise, () => {
				abortController.abort();
				return Promise.resolve();
			});
		},
		shaka.net.NetworkingEngine.PluginPriority.APPLICATION
	);

	cacheInitialized = true;
}

export async function loadShaka(): Promise<ShakaModule> {
	if (!import.meta.client) {
		throw new Error('Shaka Player can only be loaded on the client');
	}

	if (shakaInstance) {
		return shakaInstance;
	}

	const shakaModule = await import('shaka-player');
	shakaInstance = (shakaModule.default ||
		shakaModule) as unknown as ShakaModule;
	shakaInstance.polyfill.installAll();
	initSegmentCache(shakaInstance);
	return shakaInstance;
}

export function isBrowserSupported(): boolean {
	return shakaInstance?.Player.isBrowserSupported() ?? false;
}

export { clearVideoCache } from './videoSegmentCache';
